import { Outlet, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Leaf, Menu, X, Globe, ShoppingCart, Trash2, ArrowRight, User, Search, Linkedin } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import { cn, calculateItemPrice } from '../lib/utils';
import { getLocalizedValue } from '../lib/utils/localization';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { SEO } from './SEO';

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

export function Layout() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const { items, totalItems, totalAmount, removeItem, updateQuantity } = useCart();
  const { user } = useAuth();
  const userRole = user?.role || 'client';
  const cartRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (cartRef.current && !cartRef.current.contains(event.target as Node)) {
        setIsCartOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const navigation = [
    { name: t('nav.home', 'Inicio'), href: '/' },
    { name: t('nav.directory'), href: '/directory' },
    { name: t('nav.quote'), href: '/quote' },
    { name: t('nav.marketplace', 'Marketplace'), href: '/marketplace' },
    { name: t('nav.pricing', 'Planes'), href: '/pricing' },
    { name: t('nav.jobs', 'Empleo'), href: '/jobs' },
    { name: t('nav.contact', 'Contacto'), href: '/contact' },
  ];

  const changeLanguage = (lng: string) => {
    localStorage.setItem('userExplicitLang', 'true');
    i18n.changeLanguage(lng);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <SEO />
      <ScrollToTop />
      <header className="bg-white flex flex-col shadow-sm relative z-50">
        {/* Top Bar */}
        <div className="border-b border-gray-100">
          <div className="max-w-none mx-auto px-4 sm:px-6 lg:px-12">
            <div className="flex items-center justify-between h-20 md:h-24 gap-4 md:gap-8">
              {/* Logo */}
              <Link to="/" className="flex-shrink-0 flex items-center">
                <img 
                  src="/logo.svg" 
                  alt={t('layout.logoAlt', 'AGP Logo')} 
                  className="h-12 md:h-16 w-auto object-contain" 
                  width="256"
                  height="64"
                />
              </Link>

              {/* Desktop Search */}
              <div className="hidden md:flex flex-1 max-w-3xl">
                <div className="relative flex items-center w-full">
                  <input
                    type="text"
                    placeholder={t('layout.searchPlaceholder')}
                    className="w-full pl-5 pr-12 py-3.5 rounded-l-xl border-2 border-emerald-600 focus:outline-none focus:ring-0 focus:border-emerald-600 text-gray-900"
                  />
                  <button 
                    aria-label={t('layout.searchPlaceholder', 'Buscar')}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-3.5 rounded-r-xl border-2 border-emerald-600 transition-colors flex items-center justify-center"
                  >
                    <Search className="h-5 w-5" />
                  </button>
                </div>
              </div>

              {/* Right Actions */}
              <div className="flex items-center gap-4 md:gap-6">
                {/* Language (Desktop only) */}
                <div className="hidden lg:flex items-center gap-1 text-sm text-gray-600 hover:text-emerald-600 transition-colors">
                  <Globe className="h-5 w-5" />
                  <select
                    className="bg-transparent border-none focus:ring-0 cursor-pointer font-medium text-gray-700"
                    value={i18n.language}
                    onChange={(e) => changeLanguage(e.target.value)}
                  >
                    <option value="es">{t('layout.language.es')}</option>
                    <option value="en">{t('layout.language.en')}</option>
                    <option value="fr">{t('layout.language.fr')}</option>
                    <option value="it">{t('layout.language.it')}</option>
                    <option value="pt">{t('layout.language.pt')}</option>
                    <option value="de">{t('layout.language.de')}</option>
                    <option value="nl">{t('layout.language.nl')}</option>
                  </select>
                </div>

                {/* Account */}
                <Link
                  to={user ? "/dashboard" : "/my-account"}
                  className="flex items-center gap-3 text-gray-600 hover:text-emerald-600 transition-colors"
                >
                  <div className="bg-gray-100 p-2.5 rounded-full">
                    <User className="h-5 w-5" />
                  </div>
                  <div className="hidden lg:flex flex-col text-left">
                    {user ? (
                      <>
                        <span className="text-[11px] text-gray-500 leading-none mb-1">{user.role === 'admin' ? 'Hello admin' : t('layout.account.hello')}</span>
                        <span className="text-sm font-bold text-gray-900 leading-none truncate max-w-[100px]">{user.name}</span>
                      </>
                    ) : (
                      <>
                        <span className="text-[11px] text-gray-500 leading-none mb-1">{t('layout.account.loginRegister')}</span>
                        <span className="text-sm font-bold text-gray-900 leading-none">{t('layout.account.myAccount')}</span>
                      </>
                    )}
                  </div>
                </Link>

                {/* Cart */}
                <div className="relative" ref={cartRef}>
                  <button
                    onClick={() => setIsCartOpen(!isCartOpen)}
                    aria-label={t('layout.cart.myCart', 'Mi carrito')}
                    className="flex items-center gap-3 text-gray-600 hover:text-emerald-600 transition-colors"
                  >
                    <div className="relative bg-emerald-50 p-2.5 rounded-full text-emerald-600">
                      <ShoppingCart className="h-5 w-5" />
                      {totalItems > 0 && (
                        <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold h-5 w-5 rounded-full flex items-center justify-center border-2 border-white">
                          {totalItems}
                        </span>
                      )}
                    </div>
                    <div className="hidden lg:flex flex-col text-left">
                      <span className="text-[11px] text-gray-500 leading-none mb-1">{t('layout.cart.myCart')}</span>
                      <span className="text-sm font-bold text-gray-900 leading-none">{(Number(totalAmount) || 0).toFixed(2)}€</span>
                    </div>
                  </button>

                  {/* Cart Dropdown */}
                  {isCartOpen && (
                    <div className="absolute right-0 mt-4 w-80 bg-white rounded-2xl shadow-xl border border-gray-100 z-50 overflow-hidden">
                      <div className="p-4 border-b border-gray-100 bg-gray-50/50">
                        <h3 className="font-bold text-gray-900">{t('layout.cart.myCart')}</h3>
                      </div>
                      <div className="max-h-96 overflow-y-auto p-4 space-y-4">
                        {items.length === 0 ? (
                          <p className="text-center text-gray-500 text-sm py-4">{t('layout.cart.empty')}</p>
                        ) : (
                          items.map((item, idx) => (
                            <div key={idx} className="flex gap-3">
                              <div className="w-12 h-12 bg-gray-100 rounded-lg overflow-hidden shrink-0">
                                <img src={item.product.images?.[0] || 'https://picsum.photos/seed/product/800/600'} alt={getLocalizedValue(item.product.name, i18n.language)} className="w-full h-full object-cover" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-bold text-gray-900 truncate">{getLocalizedValue(item.product.name, i18n.language)}</p>
                                <div className="flex items-center justify-between mt-1">
                                  <div className="flex items-center gap-2">
                                    <button 
                                      onClick={() => updateQuantity(item.product.id, item.quantity - 1, item.variantId)}
                                      aria-label={t('common.decrease', 'Disminuir')}
                                      className="text-gray-400 hover:text-emerald-600"
                                    >-</button>
                                    <span className="text-xs font-medium">{item.quantity}</span>
                                    <button 
                                      onClick={() => updateQuantity(item.product.id, item.quantity + 1, item.variantId)}
                                      aria-label={t('common.increase', 'Aumentar')}
                                      className="text-gray-400 hover:text-emerald-600"
                                    >+</button>
                                  </div>
                                  <span className="text-sm font-bold text-gray-900">
                                    {(() => {
                                      const price = calculateItemPrice(item.product, item.variantId, userRole);
                                      return (price * (Number(item.quantity) || 0)).toFixed(2);
                                    })()}€
                                  </span>
                                </div>
                              </div>
                              <button 
                                onClick={() => removeItem(item.product.id, item.variantId)}
                                aria-label={t('common.remove', 'Eliminar')}
                                className="text-gray-400 hover:text-red-500"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          ))
                        )}
                      </div>
                      {items.length > 0 && (
                        <div className="p-4 border-t border-gray-100 bg-gray-50/50">
                          <div className="flex justify-between mb-4">
                            <span className="text-gray-600 font-medium">{t('layout.cart.total')}</span>
                            <span className="text-lg font-bold text-gray-900">{(Number(totalAmount) || 0).toFixed(2)}€</span>
                          </div>
                          <Link
                            to="/cart"
                            onClick={() => setIsCartOpen(false)}
                            className="w-full bg-emerald-600 text-white py-3 rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-emerald-700 transition-colors"
                          >
                            {t('layout.cart.viewCart')}
                            <ArrowRight className="h-4 w-4" />
                          </Link>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Mobile Menu Button */}
                <button
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                  aria-label={t('layout.menu', 'Menú')}
                  className="md:hidden p-2 text-gray-600 hover:text-emerald-600"
                >
                  {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                </button>
              </div>
            </div>

            {/* Mobile Search (Visible only on small screens) */}
            <div className="md:hidden pb-4">
              <div className="relative flex items-center w-full">
                <input
                  type="text"
                  placeholder={t('layout.searchPlaceholder')}
                  className="w-full pl-4 pr-12 py-2.5 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 text-gray-900"
                />
                <button 
                  aria-label={t('layout.searchPlaceholder', 'Buscar')}
                  className="absolute right-0 top-0 h-full px-4 text-gray-400 hover:text-emerald-600 flex items-center justify-center"
                >
                  <Search className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Bar */}
        <nav className="bg-emerald-700 hidden md:block">
          <div className="max-w-none mx-auto px-4 sm:px-6 lg:px-12">
            <div className="flex items-center justify-center space-x-8 h-14">
              {navigation.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  className={cn(
                    "text-sm font-bold transition-all hover:text-emerald-200",
                    location.pathname === item.href ? "text-white border-b-2 border-white py-4" : "text-emerald-50 py-4 border-b-2 border-transparent"
                  )}
                >
                  {item.name}
                </Link>
              ))}
            </div>
          </div>
        </nav>

        {/* Mobile Navigation Menu */}
        {isMenuOpen && (
          <div className="md:hidden bg-white border-b border-gray-200 absolute w-full z-40 shadow-xl">
            <div className="px-4 pt-2 pb-4 space-y-1">
              {navigation.map((item) => (
                <Link
                  key={item.href}
                  to={item.href}
                  className={cn(
                    "block px-3 py-3 rounded-lg text-base font-bold",
                    location.pathname === item.href
                      ? "bg-emerald-50 text-emerald-700"
                      : "text-gray-700 hover:bg-gray-50 hover:text-emerald-600"
                  )}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
              {/* Mobile Language Selector */}
              <div className="px-3 py-3 flex items-center gap-2 text-gray-700 border-t border-gray-100 mt-2">
                <Globe className="h-5 w-5" />
                <select
                  className="bg-transparent border-none focus:ring-0 cursor-pointer font-bold w-full"
                  value={i18n.language}
                  onChange={(e) => changeLanguage(e.target.value)}
                >
                  <option value="es">{t('layout.language.es_full')}</option>
                  <option value="en">{t('layout.language.en_full')}</option>
                  <option value="fr">{t('layout.language.fr_full')}</option>
                  <option value="it">{t('layout.language.it_full')}</option>
                  <option value="pt">{t('layout.language.pt_full')}</option>
                  <option value="de">{t('layout.language.de_full')}</option>
                  <option value="nl">{t('layout.language.nl_full')}</option>
                </select>
              </div>
            </div>
          </div>
        )}
      </header>

      <main className={cn(
        "flex-1 w-full",
        ['/marketplace', '/quote'].includes(location.pathname) ? "px-0 pt-0 pb-12" : "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8"
      )}>
        <Outlet />
      </main>
      
      <footer className="bg-white border-t border-gray-200 mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            {/* Column 1: Logo & Description */}
            <div className="col-span-1">
              <div className="flex items-center gap-2 mb-6">
                <img 
                  src="/logo.svg" 
                  alt="AGP Logo" 
                  className="h-16 w-auto object-contain" 
                  width="256"
                  height="64"
                />
              </div>
              <p className="text-sm text-gray-500 leading-relaxed">
                {t('layout.footer.description')}
              </p>
            </div>
            
            {/* Column 2: Plataforma */}
            <div>
              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-4">{t('layout.footer.platform')}</h3>
              <ul className="space-y-3">
                <li>
                  <Link to="/dashboard" className="text-sm text-gray-600 hover:text-emerald-600 transition-colors">{t('layout.footer.links.myAccount')}</Link>
                </li>
                <li>
                  <Link to="/marketplace" className="text-sm text-gray-600 hover:text-emerald-600 transition-colors">{t('layout.footer.links.marketplace')}</Link>
                </li>
                <li>
                  <Link to="/directory" className="text-sm text-gray-600 hover:text-emerald-600 transition-colors">{t('layout.footer.links.directory')}</Link>
                </li>
                <li>
                  <Link to="/track-order" className="text-sm text-gray-600 hover:text-emerald-600 transition-colors">{t('layout.footer.links.trackOrder')}</Link>
                </li>
                <li>
                  <Link to="/jobs" className="text-sm text-gray-600 hover:text-emerald-600 transition-colors">{t('layout.footer.links.jobs')}</Link>
                </li>
              </ul>
            </div>

            {/* Column 3: Soporte */}
            <div>
              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-4">{t('layout.footer.support')}</h3>
              <ul className="space-y-3">
                <li>
                  <Link to="/faq" className="text-sm text-gray-600 hover:text-emerald-600 transition-colors">{t('layout.footer.links.faq')}</Link>
                </li>
                <li>
                  <Link to="/rates-and-benefits" className="text-sm text-gray-600 hover:text-emerald-600 transition-colors">Tarifas y Beneficios</Link>
                </li>
                <li>
                  <Link to="/contact" className="text-sm text-gray-600 hover:text-emerald-600 transition-colors">{t('layout.footer.links.contact')}</Link>
                </li>
              </ul>
            </div>

            {/* Column 4: Legal */}
            <div>
              <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-4">{t('layout.footer.legal')}</h3>
              <ul className="space-y-3">
                <li>
                  <Link to="/terms" className="text-sm text-gray-600 hover:text-emerald-600 transition-colors">{t('layout.footer.links.terms')}</Link>
                </li>
                <li>
                  <Link to="/privacy" className="text-sm text-gray-600 hover:text-emerald-600 transition-colors">{t('layout.footer.links.privacy')}</Link>
                </li>
                <li>
                  <Link to="/cookies" className="text-sm text-gray-600 hover:text-emerald-600 transition-colors">{t('layout.footer.links.cookies')}</Link>
                </li>
                <li>
                  <Link to="/legal" className="text-sm text-gray-600 hover:text-emerald-600 transition-colors">{t('layout.footer.links.legalNotice')}</Link>
                </li>
                <li>
                  <Link to="/seller-agreement" className="text-sm text-gray-600 hover:text-emerald-600 transition-colors">{t('layout.footer.links.sellerAgreement')}</Link>
                </li>
                <li>
                  <Link to="/payment-policy" className="text-sm text-gray-600 hover:text-emerald-600 transition-colors">{t('layout.footer.links.paymentPolicy')}</Link>
                </li>
                <li>
                  <Link to="/anti-fraud" className="text-sm text-gray-600 hover:text-emerald-600 transition-colors">{t('layout.footer.links.antiFraud')}</Link>
                </li>
              </ul>
            </div>
          </div>

          <div className="pt-8 border-t border-gray-200 flex flex-col items-center gap-8">
            <div className="flex flex-col items-center gap-4">
              <span className="text-sm font-medium text-gray-500">{t('layout.footer.paymentMethods')}</span>
              <div className="flex flex-wrap items-center justify-center gap-2">
                {/* Visa */}
                <div className="h-8 px-3 bg-white border border-gray-200 rounded flex items-center justify-center text-[#1434CB] font-bold text-sm shadow-sm" title={t('layout.footer.payment.visa', 'VISA')}>VISA</div>
                {/* Mastercard */}
                <div className="h-8 px-3 bg-white border border-gray-200 rounded flex items-center justify-center shadow-sm" title={t('layout.footer.payment.mastercard', 'Mastercard')}>
                  <div className="flex items-center">
                    <div className="w-4 h-4 rounded-full bg-[#EB001B] opacity-90"></div>
                    <div className="w-4 h-4 rounded-full bg-[#F79E1B] opacity-90 -ml-2"></div>
                  </div>
                </div>
                {/* Amazon Pay */}
                <div className="h-8 px-3 bg-white border border-gray-200 rounded flex items-center justify-center text-gray-900 font-bold text-xs shadow-sm" title={t('layout.footer.payment.amazonPay', 'Amazon Pay')}>
                  amazon<span className="text-[#FF9900]">pay</span>
                </div>
                {/* Apple Pay */}
                <div className="h-8 px-3 bg-black rounded flex items-center justify-center text-white font-medium text-xs shadow-sm gap-1" title={t('layout.footer.payment.applePay', 'Apple Pay')}>
                  <svg className="w-3 h-3 fill-current" viewBox="0 0 384 512"><path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/></svg>
                  Pay
                </div>
                {/* Google Pay */}
                <div className="h-8 px-3 bg-white border border-gray-200 rounded flex items-center justify-center text-gray-600 font-medium text-xs shadow-sm gap-1" title={t('layout.footer.payment.googlePay', 'Google Pay')}>
                  <svg className="w-3 h-3" viewBox="0 0 488 512"><path fill="#4285F4" d="M488 261.8C488 403.3 391.1 504 248 504 110.8 504 0 393.2 0 256S110.8 8 248 8c66.8 0 123 24.5 166.3 64.9l-67.5 64.9C258.5 52.6 94.3 116.6 94.3 256c0 86.5 69.1 156.6 153.7 156.6 98.2 0 135-70.4 140.8-106.9H248v-85.3h236.1c2.3 12.7 3.9 24.9 3.9 41.4z"/></svg>
                  Pay
                </div>
                {/* Link */}
                <div className="h-8 px-3 bg-[#00D66F] rounded flex items-center justify-center text-white font-bold text-sm shadow-sm" title={t('layout.footer.payment.link', 'Link')}>
                  Link
                </div>
                {/* MB WAY */}
                <div className="h-8 px-3 bg-white border border-gray-200 rounded flex items-center justify-center text-[#0071CE] font-bold text-xs shadow-sm" title={t('layout.footer.payment.mbWay', 'MB WAY')}>
                  MB WAY
                </div>
                {/* Bancontact */}
                <div className="h-8 px-3 bg-white border border-gray-200 rounded flex items-center justify-center text-[#0052A3] font-bold text-xs shadow-sm" title={t('layout.footer.payment.bancontact', 'Bancontact')}>
                  Bancontact
                </div>
                {/* BLIK */}
                <div className="h-8 px-3 bg-white border border-gray-200 rounded flex items-center justify-center shadow-sm" title={t('layout.footer.payment.blik', 'BLIK')}>
                  <div className="flex items-center gap-1">
                    <div className="w-3 h-3 bg-black rounded-sm"></div>
                    <span className="text-black font-bold text-xs">BLIK</span>
                  </div>
                </div>
                {/* EPS */}
                <div className="h-8 px-3 bg-white border border-gray-200 rounded flex items-center justify-center text-[#0052A3] font-bold text-xs italic shadow-sm" title={t('layout.footer.payment.eps', 'EPS')}>
                  eps
                </div>
                {/* SEPA */}
                <div className="h-8 px-3 bg-white border border-gray-200 rounded flex items-center justify-center text-blue-800 font-bold text-xs shadow-sm" title={t('layout.footer.payment.sepa', 'SEPA')}>
                  SEPA
                </div>
              </div>
            </div>
            
            <div className="w-full flex flex-col md:flex-row justify-between items-center gap-4 pt-6 border-t border-gray-100">
              <p className="text-sm text-gray-500">
                &copy; {new Date().getFullYear()} {t('layout.footer.rights')}
              </p>
              <div className="flex items-center gap-4">
                <a 
                  href="https://www.linkedin.com/groups/3671473/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-gray-400 hover:text-[#0A66C2] transition-colors"
                  title={t('layout.footer.linkedinTitle', 'AGP LinkedIn Group')}
                >
                  <Linkedin className="h-5 w-5" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
